import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { styled } from '@mui/material'

import 'mapbox-gl/dist/mapbox-gl.css'
import Map, {
  Marker, Popup, Source, Layer, FullscreenControl, NavigationControl,
} from 'react-map-gl'
import type { MapRef, MapProps, SymbolLayer, MapboxEvent } from 'react-map-gl'

import GpsClipboard from './views/node/GpsClipboard'
import config from '/config'
import settings from './settings'

import * as formatters from '/components/views/nodes/nodeFormatters'
import * as BK from '/components/apis/beekeeper'
import { useColorScheme } from '@mui/material'

const {initialViewState} = settings


const DISABLE_MAP = config['disableMaps'] || false
const MAPBOX_TOKEN = process.env.MAPBOX_TOKEN ||
  'pk.eyJ1IjoibmNvbnJhZGFubCIsImEiOiJjbGt3dXhod2UxMzAyM2dwcnl4ZGJubnd1In0.' +
  'FNVNhHfEIZ2qjy8mqFQmQw'


const markerSize = 10

const mapSettings = {
  ...(initialViewState ? {initialViewState} : {
    initialViewState: {
      longitude: -99,
      latitude: 38.5,
      zoom: 3.0
    }
  }),
  mapboxAccessToken: MAPBOX_TOKEN,
  style: {height: '350px'},
  mapStyle: 'mapbox://styles/mapbox/dark-v9',
  cooperativeGestures: true,
  /* 3d view
  projection: 'globe',
  fog: {}
  */
} as MapProps

const fitBoundsPadding = {top: 35, bottom: 35, left: 60, right: 60}


const getBBox = (coordinates) : [[number, number], [number, number]] => {
  const lngs = coordinates.map(o => o.lng)
  const lats = coordinates.map(o => o.lat)

  const lngMin = Math.min(...lngs)
  const lngMax = Math.max(...lngs)
  const latMin = Math.min(...lats)
  const latMax = Math.max(...lats)

  return [[lngMin, latMin], [lngMax, latMax]]
}



const getValidCoords = (data: Data[]) =>
  (data || []).filter(({lat, lng}) => lat && lng)
    .map(({lat, lng}) => ({lng, lat}))



const getGeoSpec = (data: Data[]) => {
  const features = (data || [])
    .filter(({lat, lng}) => lat && lng)
    .map(obj => ({
      type: 'Feature',
      status: obj.status,
      geometry: {
        type: 'Point',
        coordinates: [obj.lng, obj.lat]
      },
      properties: {
        title: formatters.vsnToDisplayStrAlt(obj.vsn, obj.site_id),
        description: '',
        data: obj
      },
    }))

  return {
    type: 'FeatureCollection',
    features
  }
}


const getLayerStyle = (isDark: boolean): SymbolLayer => ({
  'id': 'marker-labels',
  'type': 'symbol',
  'source': 'geoSpec',
  'layout': {
    'text-field': [
      'format',
      ['upcase', ['get', 'title']],
      { 'font-scale': 0.9 },
    ],
    'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
    'text-radial-offset': 0.5,
    'text-justify': 'auto',
    'icon-image': ['get', 'icon'],
    'text-font': [
      'Open Sans Semibold',
      'Arial Unicode MS Bold'
    ],
  },
  paint: {
    'text-color': isDark ? '#ddd' : '#444'
  }
})



type PopupProps = {
  data: Data
  onClose: () => void
  showUptime: boolean
}

function PopupInfo(props: PopupProps) {
  const {data, onClose, showUptime} = props

  // todo(nc): use new manifests endpoint when avail
  // @ts-ignore; use new manifests endpoint when avail
  const {vsn, focus, city, cityStr, state, status, lng, lat} = data || {}

  return (
    <Popup
      longitude={lng}
      latitude={lat}
      onClose={onClose}
      closeButton={false}
      closeOnClick
    >
      <h2 className="no-margin flex justify-between">
        <div>
          {vsn.charAt(0) == 'V' &&
            'Sage Blade'
          }
          {vsn.charAt(0) == 'W' &&
            'Wild Sage Node'
          }&nbsp;
          {formatters.vsnLink(vsn, data)}
        </div>
      </h2>

      <div className="flex gap-2 items-center node-meta">
        {showUptime &&
          <div className="flex column status">
            <small className="muted font-bold">Status</small>
            {formatters.statusWithPhase(status, data)}
          </div>
        }

        {focus &&
          <div>
            <small className="muted font-bold">Focus</small>
            <div>
              <Link to={`/nodes/?focus="${encodeURIComponent(focus)}"`}>
                {focus}
              </Link>
            </div>
          </div>
        }


        <div>
          <small className="muted font-bold">City</small>
          <div>
            {city && cityStr &&
              <Link to={`/nodes?city="${encodeURIComponent(city)}"`}>
                {cityStr}
              </Link>
            }
            {state && <>,{' '}
              <Link to={`/nodes?state="${encodeURIComponent(state)}"`}>
                {state}
              </Link>
            </>}
            {!(city && state) && '-'}
          </div>
        </div>

        <GpsClipboard data={data} />
      </div>
    </Popup>
  )
}


type Data = BK.NodeState & {
  elapsedTimes: {[device: string]: number }
}

type Props = {
  data: Data[]
  markerClass?: 'blue-dot'
  updateID?: number | string
  showUptime?: boolean
  autoFitBounds?: boolean
  mapSettings?: Partial<MapProps>
}


export default function MapGL(props: Props) {
  const {
    data = null,
    markerClass,
    updateID,
    showUptime = true,
    autoFitBounds = true,
    mapSettings: mapSettingsOverride
  } = props

  const {mode, systemMode} = useColorScheme()
  const mapRef = useRef<MapRef>(null)
  const [popup, setPopup] = useState(null)

  const isDarkMode = (mode === 'system' ? systemMode : mode) === 'dark'
  const layerStyle = getLayerStyle(isDarkMode)

  const [geoData, setGeoData] = useState(null)
  const [markers, setMarkers] = useState<Data[]>([])

  // keep track of primary keys
  const [lastID, setLastID] = useState<number | string>(-1)

  // just determine when to update for now
  useEffect(() => {
    if (DISABLE_MAP) return

    const d = data
    const geoData = getGeoSpec(d)
    const coords = getValidCoords(d)
    const bbox = getBBox(coords)

    setGeoData(geoData)
    setMarkers(d)

    if (autoFitBounds && mapRef.current && coords.length && lastID !== updateID) {
      mapRef.current.on('load', () => {
        mapRef.current.fitBounds(bbox, { padding: fitBoundsPadding, maxZoom: 10 })
      })
    }

    setLastID(updateID)

  }, [data, autoFitBounds])


  useEffect(() => {
    if (!data?.length || lastID === updateID || !mapRef.current) return

    const d = data
    const coords = getValidCoords(d)

    if (!coords.length) return

    const bbox = getBBox(coords)

    mapRef.current.fitBounds(bbox, { padding: fitBoundsPadding, maxZoom: 10 })

  }, [updateID, data, lastID])


  const handleClick = (evt: MapboxEvent<MouseEvent>, obj: Data) => {
    evt.originalEvent.stopPropagation()
    setPopup(obj)
  }

  return (
    <Root>
      <Map ref={mapRef}
        {...{...mapSettings, ...mapSettingsOverride}}
        mapStyle={
          `mapbox://styles/mapbox/${(mode === 'system' ? systemMode : mode) === 'dark' ? 'dark-v10' : 'light-v9'}`
        }>
        <FullscreenControl />
        <NavigationControl position="bottom-right" showCompass={false} />

        {geoData &&
          <Source id="markers" type="geojson" data={geoData}>
            <Layer {...layerStyle} />
          </Source>
        }

        {(markers || [])
          .filter(o => o.lng && o.lat)
          .map(obj => {
            const {vsn, lng, lat, status} = obj
            return (
              <Marker key={vsn}
                longitude={lng}
                latitude={lat}
                onClick={(evt) => handleClick(evt, obj)}
              >
                <div
                  className={
                    `marker-dot ${
                      markerClass ? `marker-${markerClass}` : `marker-${(status || '').replace(/ /g, '-')}`
                    }`
                  }>
                </div>
              </Marker>
            )
          })}

        {popup &&
          <PopupInfo data={popup} onClose={() => setPopup(null)} showUptime={showUptime} />
        }
      </Map>
    </Root>
  )
}


const Root = styled('div')(({ theme }) => `
  width: 100%;

  .popup-title {
    margin-top: 0;
  }

  .mapboxgl-marker {
    cursor: pointer;
  }

  .marker-dot {
    height: ${markerSize}px;
    width: ${markerSize}px;
    border: 1px solid ${theme.palette.mode === 'dark' ? '#999' : '#666'};
    border-radius: 50%;
    display: inline-block;
  }

  .marker-reporting  {
    background: #3ac37e;
    border: 1px solid #2b9962;
    opacity: .65;
  }

  .marker-degraded {
    background: hsl(41, 83%, 35%);
  }

  .marker-not-reporting {
    background: #d72020;
    border: 1px solid #992727;
    opacity: .65;
  }

  .marker-blue-dot {
    background: rgb(31, 119, 180);
    border: 1px solid rgb(24, 99, 152);
    opacity: .65;
  }

  .mapboxgl-popup {
    max-width: 100% !important;

    .mapboxgl-popup-content {
      background: ${theme.palette.background.paper};
      color: ${theme.palette.text.primary};
    }

    .mapboxgl-popup-tip {
      border-top-color: ${theme.palette.background.paper};
    }

    .node-meta {
      white-space: nowrap;
      font-size: 1.2em;
    }

    .status {
      .MuiSvgIcon-root {
        font-size: 19px;
      }
    }

    .muted {
      color: ${theme.palette.text.secondary};
    }
  }

  .clipboard-content {
    margin-right: 20px;
  }
`)
